import { MouseEvent, ReactElement, useCallback, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { convertISODateTODDMMYY } from '@helper/utils';
import { RigidUserObject } from '@services/hooks/endusers/rigitAllUser';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { Modal } from '@components/common/modal/modal';
import { RigidStatusAction, RigidStatusModal } from './rigidStatusModal';
import { useIsImpersonationLoginUser } from '../../../../authGuard/hooks/useIsImpersonationLoginUser';

interface IRigidUserCard {
	index: number;
	userObject: RigidUserObject;
	state: string;
	refetchRigidUsers?: () => void;
}

export const RigidUserCard = ({ index, userObject, state, refetchRigidUsers }: IRigidUserCard): ReactElement => {
	const navigate = useNavigate();
	const userRoleType = useUserRoleType();
	const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
	const [statusAction, setStatusAction] = useState<RigidStatusAction>('Approved');
	const isImpersonationLoginUser = useIsImpersonationLoginUser();

	const handleNavigate = useCallback(() => {
		const pageRouteName = userRoleType === 'RBDM' ? '/rbdm/contractor' : '/contractor';

		navigate(pageRouteName, {
			state: {
				userData: userObject,
				path: state,
			},
		});
	}, [navigate, state, userObject, userRoleType]);

	const handleStatusModalOpen = useCallback((event: MouseEvent<HTMLButtonElement>, action: RigidStatusAction): void => {
		event.stopPropagation();
		setStatusAction(action);
		setIsStatusModalOpen(true);
	}, []);

	const handleStatusModalClose = useCallback((): void => {
		setIsStatusModalOpen(false);
	}, []);

	return (
		<li key={index} className="d-col d-col-2">
			<div
				role="button"
				tabIndex={0}
				onClick={handleNavigate}
				onKeyDown={(event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						handleNavigate();
					}
				}}
				className="user-card-inner-container"
			>
				<fieldset>
					<legend title={userObject.name}>{userObject.name}</legend>
					<span className="h5 user-id">ID: {userObject.membershipno}</span>
					<div className="user-about-details adopted-list">
						<div className="card-upper-details">
							<span className="club-name" title={userObject.clubname ?? ''}>
								Club: {userObject.clubname}
							</span>
							<NavLink
								to={`tel:+91${userObject.phone}`}
								aria-label="call user link"
								className="call-icon"
								onClick={(e) => e.stopPropagation()}
							>
								<span className="icon-call"></span>
							</NavLink>
						</div>
						<div className="user-bottom-details row">
							<div className="left-content d-col d-col-12">
								<p className="h5" title={userObject.variable_contractor_type_c ?? ''}>
									Type: {userObject.variable_contractor_type_c ?? '-'}
								</p>
							</div>
							<div className="card-footer-row card-footer--all d-col d-col-12">
								<span className="h5 all-footer-added">
									Added: {convertISODateTODDMMYY(userObject.createddate ?? '')}
								</span>
							</div>
							{userObject.venuestatus_c === 'Approved' && (
								<div className="h5 user-rigid-status user-rigid-approved d-col d-col-12">
									<div>Approve</div>
								</div>
							)}
							{userObject.venuestatus_c === 'Rejected' && (
								<div className="h5 user-rigid-status user-rigid-rejected d-col d-col-12">
									<div>Reject</div>
								</div>
							)}

							{userObject.venuestatus_c !== 'Approved' && userObject.venuestatus_c !== 'Rejected' && (
								<div className="rigid-contractor-status-buttons">
									<button
										type="button"
										className="btn btn-primary btn-small"
										disabled={isImpersonationLoginUser}
										aria-label="approve rigid contractor"
										onClick={(event) => handleStatusModalOpen(event, 'Approved')}
									>
										Approve
									</button>
									<button
										type="button"
										className="btn btn-primary btn-small"
										disabled={isImpersonationLoginUser}
										aria-label="reject rigid contractor"
										onClick={(event) => handleStatusModalOpen(event, 'Rejected')}
									>
										Reject
									</button>
								</div>
							)}
						</div>
					</div>
				</fieldset>
			</div>

			<Modal
				modalOpen={isStatusModalOpen}
				modalId={`rigidStatusModal-${userObject.id}`}
				isEscAllow={false}
				className="dialog-lg dialog-bottom dialog-conform"
				modalTitle={statusAction === 'Approved' ? 'Approve Contractor' : 'Reject Contractor'}
				onModalClose={handleStatusModalClose}
			>
				{isStatusModalOpen && (
					<RigidStatusModal
						closeModal={handleStatusModalClose}
						action={statusAction}
						contractorId={userObject.id}
						contractorSfid={userObject.sfid}
						refetchRigidUsers={refetchRigidUsers}
					/>
				)}
			</Modal>
		</li>
	);
};
